import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context';

import { getCookie } from 'cookies-next';
import { setCookie, deleteCookie } from '@/src/utils/cookie';

import { Amplify, Auth } from 'aws-amplify';
import awsConfig from '../utils/awsExports';
import { FederatedSignInOptions } from '@aws-amplify/auth/lib-esm/types';

import { getQueryClient } from '@/src/v2/service/graphql/api';
import { GlobalSignOutV2 } from '../v2/service/v2/graphql/apiv2';
import { IUserResponseV2 } from '../v2/service/v2/graphql/generated/gql-api.generated';
import axios from 'axios';

// Initializes AWS Amplify
Amplify.configure(awsConfig);

// Initiates federated sign-in with specified providers (Google, Twitter, Discord).
export const socialAuthService = (
  provider: 'Google' | 'Twitter' | 'Discord'
) => {
  return Auth.federatedSignIn({ provider } as FederatedSignInOptions);
};
export const signInService = async (username: string, password: string) => {
  // Retry function to handle intermittent authentication failures
  const retrySignIn = async (
    username: string,
    password: string,
    retries = 3,
    delay = 2000
  ) => {
    for (let i = 0; i < retries; i++) {
      try {
        // Attempt to sign in
        const response = await Auth.signIn({ username, password });
        return response; // Return the response if successful
      } catch (error) {
        if (i === retries - 1) throw error; // Throw error on the last attempt
        // Wait for a specified delay before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  };

  try {
    const channel = new BroadcastChannel('app_channel'); // Create a broadcast channel for cross-tab communication

    // Log out the user globally to prevent session conflicts
    await GlobalSignOutV2({ username });

    // Retry sign-in logic with 3 attempts and a 2-second delay
    const response = await retrySignIn(username, password);

    // Set user details in cookies and localStorage
    setCookie('user', username, 15);
    setCookie('klic-email', username, 15);

    // Extract the ID token from the sign-in response
    const idToken = response.idToken
      ? response.idToken.jwtToken
      : response.signInUserSession
      ? response.signInUserSession.accessToken.jwtToken
      : '';

    // Store user credentials in localStorage and cookies
    localStorage.setItem('klic-email', username);
    localStorage.setItem('ptoken', idToken);
    setCookie('ptoken', idToken, 15);

    // Notify other tabs about the login event
    channel.postMessage('user_logged_in');

    return response; // Return the successful sign-in response
  } catch (error) {
    console.error('Sign-in failed:', error); // Log the error for debugging purposes
    throw error; // Rethrow the error to handle it at a higher level
  }
};

// Handles user sign-up with optional attributes like country and birth date.
export const signUpService = async (
  username: string,
  password: string,
  email: string,
  country?: string,
  birthDate?: string,
  termsOfUse?: boolean
) => {
  try {
    const response = await Auth.signUp({
      username,
      password,
      attributes: {
        email,
        birthdate: birthDate,
        locale: country,
        'custom:termsOfUse': termsOfUse.toString(),
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Resends sign-up confirmation code.
export const resendSignUpService = async (userName: string) => {
  try {
    const response = await Auth.resendSignUp(userName);
    return response;
  } catch (error) {
    throw error;
  }
};

// Confirms user sign-up with a verification code.
export const confirmSignUpService = async (userName: string, code: string) => {
  try {
    const response = await Auth.confirmSignUp(userName, code);
    return response;
  } catch (error) {
    throw error;
  }
};

// Initiates password reset process.
export const forgotPasswordService = async (userName: string) => {
  try {
    const response = await Auth.forgotPassword(userName);
    return response;
  } catch (error) {
    throw error;
  }
};

// Completes password reset with a code and new password.
export const resetPasswordService = async (
  userName: string,
  code: string,
  password: string
) => {
  try {
    const response = await Auth.forgotPasswordSubmit(userName, code, password);

    return response;
  } catch (error) {
    throw error;
  }
};

// Handles global sign-out, clearing cookies and local storage, and invalidating user queries.
export const signOutServiceV2 = async () => {
  const queryClient = getQueryClient();
  try {
    const username = getCookie('klic-email');
    if (username) {
      await GlobalSignOutV2({ username: username as string });
    }
    await Auth.signOut();
  } catch (erro) {
    console.log('Error 1 ', erro);
    await Auth.signOut();
  }
  deleteCookie('klic-email');
  deleteCookie('user');
  deleteCookie('ptoken');
  await queryClient.invalidateQueries(['GetCurrentUser']);
  // localStorage.clear();
  clearLocalStorageExcept([
    'last_visited_channel',
    'klic_previous_url',
    'lockoutExpiry',
    'mode',
  ]);
};

export const clearLocalStorageExcept = (key: string[] | null) => {
  if (key !== null) {
    let setValue: any = [];
    key.map((data) => {
      const value = localStorage.getItem(data);
      setValue.push({ key: data, val: value });
    });
    localStorage.clear();
    console.log(setValue);
    setValue.map((values) => {
      localStorage.setItem(values.key, values.val);
    });
  } else {
    localStorage.clear();
  }
};
// Function to clear React Query cache
const clearCache = () => {
  const queryClient = getQueryClient(); // Create or access the query client instance
  queryClient.clear(); // Clear all cached queries
};

export const checkIfUserUsesSocialLogin = (user: IUserResponseV2) => {
  let isSocialLogin = false;
  if (user) {
    if (
      (user?.google_account && user?.google_account.includes('google')) ||
      (user?.apple_account && user?.apple_account.includes('apple')) ||
      (user?.twitter_account && user?.twitter_account.includes('twitter')) ||
      (user?.discord_account && user?.discord_account.includes('discord'))
    ) {
      isSocialLogin = true;
    }
  }
  return isSocialLogin;
};

export const socialSignOut = () => {
  try {
    const logout = process.env.NEXT_PUBLIC_AUTH0_LOGOUT_URL || '';
    if (logout !== '') {
      axios
        .get(logout)
        .then((res) => {
          console.log('logout response');
        })
        .catch((err) => {
          console.error('logout error', err);
        });
    }
  } catch (error) {
    console.log('Execute Logout Function for Social Logins');
  }
};

// Signs out the user, with optional global sign-out, clearing cookies, and redirecting to the home page.
export const signOutService = async (
  router: AppRouterInstance,
  global = true,
  user?: IUserResponseV2 | null
) => {
  const queryClient = getQueryClient();
  try {
    if (global) {
      try {
        const username = getCookie('klic-email');
        if (username) {
          await GlobalSignOutV2({ username: username as string });
        }
        await Auth.signOut();
      } catch (erro) {
        await Auth.signOut();
      }
    } else {
      if (checkIfUserUsesSocialLogin(user)) {
        socialSignOut();
      } else {
        await Auth.signOut();
      }
    }
    deleteCookie('klic-email');
    deleteCookie('user');
    deleteCookie('ptoken');
    clearCache();
    await queryClient.invalidateQueries(['GetCurrentUser']);
    clearLocalStorageExcept([
      'last_visited_channel',
      'lockoutExpiry',
      'mode',
      'klic_previous_url',
    ]);
    // await router.push('/');
    console.log('Complete Execution');
  } catch (error) {
    await Auth.signOut();
    deleteCookie('klic-email');
    deleteCookie('user');
    deleteCookie('ptoken');
    clearCache();
    await queryClient.invalidateQueries(['GetCurrentUser']);
    clearLocalStorageExcept([
      'last_visited_channel',
      'lockoutExpiry',
      'mode',
      'klic_previous_url',
    ]);

    // throw error;
  }
};

// Changes user password.
export const changePassword = async (
  oldPassword: string,
  newPassword: string
) => {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(cognitoUser, oldPassword, newPassword);
    return true;
    // Redirect user to verification page or display a message
  } catch (error) {
    return false;
  }
};

// Changes user password.
export const updateUsername = async (username: string) => {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser();
    await Auth.updateUserAttributes(cognitoUser, {
      preferred_username: username,
    });
    return true;
    // Redirect user to verification page or display a message
  } catch (error) {
    return false;
  }
};

// Initiates email change and triggers email verification.
export const initiateEmailChange = async (newEmail) => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const data = await Auth.updateUserAttributes(user, {
      email: newEmail,
    });
    await Auth.verifyCurrentUserAttribute('email'); // Trigger email verification

    console.log('Email change initiated', data);
    // Redirect user to verification page or display a message
  } catch (error) {
    console.error('Error initiating email change', error);
  }
};

// Confirms email change with a verification code.
export const confirmEmailChange = async (code) => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.verifyCurrentUserAttributeSubmit('email', code);
    console.log('Email change confirmed');
    return true;
    // Redirect user to profile page or display a success message
  } catch (error) {
    // console.error('Error confirming email change', error);
    return false;
  }
};
